// Shared microsite channel projection math.
//
// Campaign-strategy microsites (standard + vertical, client + superadmin, and
// the media-plan chart table) all project a platform's planned budget into an
// estimated delivery metric using a benchmark rate. The unit of that benchmark
// (CPM, CPC, CPV, CPA, CPL, CPP, CPE) determines BOTH the math and the label:
//
//   CPM  -> impressions = (budget / cpm) * 1000     (cost per 1,000 impressions)
//   CPC  -> clicks      =  budget / cpc
//   CPV  -> videoViews  =  budget / cpv
//   CPA  -> conversions =  budget / cpa
//   CPL  -> leads       =  budget / cpl
//   CPP  -> points      =  budget / cpp
//   CPE  -> engagements =  budget / cpe
//
// The historical bug this module fixes: a CPM benchmark (e.g. Google Search at
// "$23.40 CPM") was misclassified as CPC because a platform-role keyword
// ("paid search") was matched BEFORE the explicit "CPM" token, so the row
// showed `budget / cpm` (641) labeled as "clicks" instead of
// `(budget / cpm) * 1000` = ~641,026 impressions. The unit carried by the rate
// itself must always win over platform-name/role heuristics.

const UNIT_METRIC = Object.freeze({
  CPM: { key: "impressions", label: "impressions", scale: 1000 },
  CPC: { key: "clicks", label: "clicks", scale: 1 },
  CPV: { key: "videoViews", label: "video views", scale: 1 },
  CPA: { key: "conversions", label: "conversions", scale: 1 },
  CPL: { key: "leads", label: "leads", scale: 1 },
  CPP: { key: "points", label: "points", scale: 1 },
  CPE: { key: "engagements", label: "engagements", scale: 1 },
});

// Extract an explicit benchmark unit token from a string such as "$23.40 CPM"
// or "cost per click". Returns a canonical unit or null when none is present.
export const resolveBenchmarkUnitToken = (value) => {
  const text = String(value == null ? "" : value).toLowerCase();
  if (!text) return null;
  if (/\bcpm\b|cost per (?:thousand|mille|1,?000)/.test(text)) return "CPM";
  if (/\bcpv\b|cost per (?:video ?view|view)/.test(text)) return "CPV";
  if (/\bcpc\b|cost per click/.test(text)) return "CPC";
  if (/\bcpa\b|cost per (?:action|acquisition)/.test(text)) return "CPA";
  if (/\bcpl\b|cost per lead/.test(text)) return "CPL";
  if (/\bcpp\b|cost per (?:point|play|purchase)/.test(text)) return "CPP";
  if (/\bcpe\b|cost per engagement/.test(text)) return "CPE";
  return null;
};

// Resolve the authoritative benchmark unit for a channel row using a strict
// precedence so an explicit unit can never be overridden by a platform guess:
//   1. unit token attached to the rate value ("$23.40 CPM")
//   2. unit token / "impression" wording in the descriptor ("cost per click")
//   3. unit-typed benchmark fields present on the row (benchmarkCpc vs Cpm)
// Returns a canonical unit, or null when nothing is explicit (caller decides —
// delivery/estimate surfaces default a bare planning-benchmark number to CPM).
//
// NOTE: there is deliberately NO platform-name / platform-role fallback here
// (no "google_search -> CPC", no "paid search -> CPC"). Those heuristics caused
// the "641 clicks" bug: a bare "$23.40" planning CPM on Google Search was
// treated as CPC. A genuine CPC benchmark always carries an explicit signal
// (a "CPC" token, a metricType/pricingModel of CPC, or a cpc-typed field), so
// dropping the platform guess never regresses real CPC rows.
export const resolveBenchmarkUnit = ({
  rateText = "",
  descriptor = "",
  cpmFieldPresent = false,
  cpcFieldPresent = false,
  cppFieldPresent = false,
} = {}) => {
  const rateToken = resolveBenchmarkUnitToken(rateText);
  if (rateToken) return rateToken;

  const descriptorText = String(descriptor || "").toLowerCase();
  const descriptorToken = resolveBenchmarkUnitToken(descriptorText);
  if (descriptorToken) return descriptorToken;
  if (/\bimpression/.test(descriptorText)) return "CPM";

  if (cppFieldPresent && !cpmFieldPresent && !cpcFieldPresent) return "CPP";
  if (cpcFieldPresent && !cpmFieldPresent) return "CPC";
  if (cpmFieldPresent && !cpcFieldPresent) return "CPM";

  return null;
};

// Normalize a CTR / engagement rate that may arrive as a percent (3.3 => 0.033)
// or an already-normalized fraction (0.033 => 0.033). Values in (0,1] are
// treated as fractions; values > 1 are treated as percentages.
export const normalizeRateFraction = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric > 1 ? numeric / 100 : numeric;
};

const emptyMetrics = () => ({
  impressions: null,
  clicks: null,
  videoViews: null,
  engagements: null,
  conversions: null,
  leads: null,
  points: null,
  primaryMetricKey: null,
  primaryMetricLabel: null,
  primaryMetricValue: null,
  calculationSource: null,
});

// Project a channel's budget into normalized delivery metrics given a benchmark
// value + unit. Optionally derives secondary metrics from a CTR / engagement
// rate. Returns all-null metrics (no confidently-wrong number) when inputs are
// invalid or the unit is unknown.
export const projectChannelMetrics = ({
  budget,
  benchmarkValue,
  benchmarkUnit,
  ctr,
  engagementRate,
  warn = true,
} = {}) => {
  const metrics = emptyMetrics();
  const budgetNumeric = Number(budget);
  const rate = Number(benchmarkValue);
  const unit = String(benchmarkUnit == null ? "" : benchmarkUnit).toUpperCase();

  if (!Number.isFinite(budgetNumeric) || budgetNumeric <= 0) return metrics;
  if (!Number.isFinite(rate) || rate <= 0) return metrics;

  const meta = UNIT_METRIC[unit];
  if (!meta) {
    if (warn && typeof console !== "undefined" && typeof console.warn === "function") {
      // Surface mismatched/unknown benchmark units in dev + test logs.
      console.warn(
        `[micrositeChannelProjection] Unknown benchmark unit "${benchmarkUnit}" for budget ${budget}; no projected metric rendered.`
      );
    }
    return metrics;
  }

  const primaryValue = Math.round((budgetNumeric / rate) * meta.scale);
  metrics[meta.key] = primaryValue;
  metrics.primaryMetricKey = meta.key;
  metrics.primaryMetricLabel = meta.label;
  metrics.primaryMetricValue = primaryValue;
  metrics.calculationSource = unit;

  // CPM yields impressions -> derive clicks (CTR) and engagements (rate).
  if (unit === "CPM") {
    const ctrFraction = normalizeRateFraction(ctr);
    if (ctrFraction) metrics.clicks = Math.round(primaryValue * ctrFraction);
    const engagementFraction = normalizeRateFraction(engagementRate);
    if (engagementFraction) metrics.engagements = Math.round(primaryValue * engagementFraction);
  }

  // CPC yields clicks -> derive impressions only when a CTR assumption exists.
  if (unit === "CPC") {
    const ctrFraction = normalizeRateFraction(ctr);
    if (ctrFraction) metrics.impressions = Math.round(primaryValue / ctrFraction);
  }

  return metrics;
};

export default projectChannelMetrics;
